'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { useLogout } from '@nx-frontend/shared/hooks/use-logout';
import { rumTrackError } from '@nx-frontend/shared/rum';
import { PortalServicePortalDto } from '@nx-frontend/shared/types';

interface NoAccessLayoutProps {
  userRole?: string;
  portal: PortalServicePortalDto | null;
  logo?: string;
}

export const NoAccessLayout = ({ userRole, portal, logo }: NoAccessLayoutProps) => {
  const logout = useLogout();
  useEffect(() => {
    if (userRole && userRole === 'client') {
      return;
    }
    rumTrackError(new Error('User with role ' + userRole + ' has no access to workspace'));
  });

  const handleLogout = () => {
    logout();
  };

  if (userRole && 'client' === userRole) {
    if (portal) {
      return (
        <div className="w-full h-full">
          <div className="flex flex-row w-full">
            <div className="bg-gray-100 h-screen flex flex-col justify-center items-center w-full">
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                {logo ? (
                  <div className="mb-6">
                    <img src={logo} alt="logo" className="mx-auto" />
                  </div>
                ) : null}
                <p className="mb-6 text-gray-600">
                  You don't have access to this workspace. <br />
                  But you can go to the portal.
                </p>
                <div className="flex flex-row justify-center items-center w-full gap-4">
                  <Button type={ButtonType.STROKED} onClick={handleLogout}>
                    Logout from account
                  </Button>
                  <Link href={`https://${portal.domain}`}>
                    <Button type={ButtonType.FLAT}>Go to Portal</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="w-full h-full">
          <div className="flex flex-row w-full">
            <div className="bg-gray-100 h-screen flex flex-col justify-center items-center w-full">
              <div className="bg-white p-8 rounded-lg shadow-md w-96 text-center">
                {logo ? (
                  <div className="mb-6">
                    <img src={logo} alt="logo" className="mx-auto" />
                  </div>
                ) : null}
                <p className="flex flex-row justify-center items-center h-full mb-6 text-gray-600">
                  You don't have access to this workspace. <br />
                  Contact the owner or manager of the organization for access.
                </p>
                <div className="flex flex-row justify-center items-center w-full">
                  <Button type={ButtonType.STROKED} onClick={handleLogout}>
                    Logout from account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="w-full h-full">
      <div className="flex flex-row w-full">
        <div className="bg-gray-100 h-screen flex flex-col justify-center items-center w-full">
          <div className="bg-white p-8 rounded-lg shadow-md w-96 text-center">
            {logo ? (
              <div className="mb-6">
                <img src={logo} alt="logo" className="mx-auto" />
              </div>
            ) : null}
            <h1 className="text-2xl font-semibold mb-4">You are not a member of this workspace.</h1>
            <p className="mb-6 text-gray-600">
              You don't have access to this workspace. Contact the owner or manager of the organization for access.
            </p>
            <div className="flex flex-row justify-center items-center w-full">
              <Button type={ButtonType.STROKED} onClick={handleLogout}>
                Logout from account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
